import { NgModule } from '@angular/core';
import { Route, RouterModule, Routes } from '@angular/router';
import { AuthGuard, CreateAuthGuard } from '@app/state';
import { environment } from '@env';
import { LayoutShellComponent } from './layout/v2/layout-shell/layout-shell.component';
import {
  AppRoutes,
  CachePresets,
  DONATE_LEGACY_REDIRECT_ROUTES,
  RouteCachePolicy,
  siteContextMatch,
  slugPageMatch,
} from '@app/core/routing';
import type { EntityKind } from '@app/shared/components/page-sections/page-view.model';
import { contentPageExistsGuard } from './features/content/content-page-renderer/content-page-exists.guard';

// Slug pages match on what the slug resolves to; the rest load by their own :slug param.
function entityPage(
  path: string,
  type: EntityKind,
  cache: RouteCachePolicy,
  isSlugPage = false
): Route {
  return {
    path,
    canMatch: isSlugPage ? [siteContextMatch, slugPageMatch(type)] : [siteContextMatch],
    data: { type, isSlugPage, metadata: { app: ['platform', 'charity'], cache } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/campaign-page/campaign-page.routes').then(
            (m) => m.CAMPAIGN_PAGE_ROUTES
          ),
      },
    ],
  };
}

const routes: Routes = [
  {
    path: 'dashboard',
    canMatch: [siteContextMatch],
    canActivate: [AuthGuard],
    data: {
      metadata: { app: ['platform', 'charity'], cache: CachePresets.NO_CACHE },
      headerShadow: true,
    },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/user-dashboard/user-dashboard.module').then(
            (m) => m.UserDashboardModule
          ),
      },
    ],
  },
  {
    // Charity host — LayoutShellComponent (charity-branded gb-header/gb-footer).
    // Auth is a focused, full-height flow with NO chrome at all: the split-screen
    // composition carries its own floating logo (Qonto-style), so both the header
    // bar and footer are disabled via route data. Both keys resolve from route
    // data, so the server and the hydrating client agree and nothing flashes.
    path: 'auth',
    canMatch: [siteContextMatch],
    data: {
      metadata: { app: ['charity', 'platform'], cache: CachePresets.NO_CACHE },
      headerEnabled: false,
      footerEnabled: false,
    },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () => import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
      },
    ],
  },
  {
    // Legacy checkout — quarantined at the checkout-v2 switchover (`src/app/_quarantined/features/donate/`).
    // `/donate/*` is kept registered purely as a redirect shim for stale bookmarks/inbound links:
    // every shape 302s to its `/checkout` equivalent, preserving the slug and query params
    // (see `donate-legacy-redirects.ts`). No `component:`/`LayoutShellComponent` needed — every
    // child redirects before anything would render.
    path: 'donate',
    canMatch: [siteContextMatch],
    children: DONATE_LEGACY_REDIRECT_ROUTES,
  },
  {
    // Universal checkout v2 — focused checkout chrome: `minimal` header (logo +
    // "Secure checkout" lock label, no Donate CTA mid-donation) and the
    // `minimal` footer (one quiet legal row + secure-checkout microlabel, see
    // `src/app/layout/v2/slots/footer/variants/footer-minimal/`).
    path: 'checkout',
    canMatch: [siteContextMatch],
    data: {
      metadata: { app: ['platform', 'charity'], cache: CachePresets.NO_CACHE },
      headerSlot: 'minimal',
      footerSlot: 'minimal',
    },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/checkout-v2/checkout-v2.routes').then((m) => m.CHECKOUT_V2_ROUTES),
      },
    ],
  },
  {
    path: 'create',
    canMatch: [siteContextMatch],
    data: {
      metadata: { app: ['platform', 'charity'], cache: CachePresets.NO_CACHE },
    },
    // component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () => import('./features/create/create.routes').then((m) => m.CREATE_ROUTES),
      },
    ],
  },
  {
    path: 'onboarding',
    canMatch: [siteContextMatch],
    data: {
      metadata: { app: ['platform'], cache: CachePresets.NO_CACHE },
    },
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/onboarding/onboarding.routes').then((m) => m.ONBOARDING_ROUTES),
      },
    ],
  },
  {
    path: 'edit',
    canMatch: [siteContextMatch],
    canActivate: [CreateAuthGuard],
    data: {
      metadata: { app: ['platform', 'charity'], cache: CachePresets.NO_CACHE },
    },
    // component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () => import('./features/create/create.routes').then((m) => m.CREATE_ROUTES),
      },
    ],
  },
  {
    path: 'pricing',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform'], cache: CachePresets.STATIC } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/content/content-page-renderer/content-page-renderer.component').then(
            (m) => m.ContentPageRendererComponent
          ),
        data: { contentSlug: 'pricing' },
      },
    ],
  },
  {
    path: 'how-it-works',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform'], cache: CachePresets.STATIC } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/content/content-page-renderer/content-page-renderer.component').then(
            (m) => m.ContentPageRendererComponent
          ),
        data: { contentSlug: 'how-it-works' },
      },
    ],
  },
  {
    path: 'givebrite-extended',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform'], cache: CachePresets.STATIC } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/content/content-page-renderer/content-page-renderer.component').then(
            (m) => m.ContentPageRendererComponent
          ),
        data: { contentSlug: 'givebrite-extended' },
      },
    ],
  },
  {
    path: 'launchgood-vs-givebrite',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform'], cache: CachePresets.STATIC } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/content/content-page-renderer/content-page-renderer.component').then(
            (m) => m.ContentPageRendererComponent
          ),
        data: { contentSlug: 'launchgood-vs-givebrite' },
      },
    ],
  },
  {
    path: 'gofundme-vs-givebrite',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform'], cache: CachePresets.STATIC } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/content/content-page-renderer/content-page-renderer.component').then(
            (m) => m.ContentPageRendererComponent
          ),
        data: { contentSlug: 'gofundme-vs-givebrite' },
      },
    ],
  },
  {
    path: 'discover',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['charity', 'platform'], cache: CachePresets.LISTING } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/shared/discover/discover.routes').then((m) => m.DISCOVER_ROUTES),
      },
    ],
  },
  {
    path: 'terms-and-conditions',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform', 'charity'], cache: CachePresets.STATIC } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/content/content-page-renderer/content-page-renderer.component').then(
            (m) => m.ContentPageRendererComponent
          ),
        data: { contentSlug: 'terms-and-conditions' },
      },
    ],
  },
  {
    path: 'privacy-policy',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform', 'charity'], cache: CachePresets.STATIC } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/content/content-page-renderer/content-page-renderer.component').then(
            (m) => m.ContentPageRendererComponent
          ),
        data: { contentSlug: 'privacy-policy' },
      },
    ],
  },
  {
    path: 'page/:slug',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform'], cache: CachePresets.STATIC } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        canActivate: [contentPageExistsGuard],
        loadComponent: () =>
          import('./features/content/content-page-renderer/content-page-renderer.component').then(
            (m) => m.ContentPageRendererComponent
          ),
      },
    ],
  },
  {
    path: 'support',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform', 'charity'], cache: CachePresets.NO_CACHE } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/content/content-page-renderer/content-page-renderer.component').then(
            (m) => m.ContentPageRendererComponent
          ),
        data: { contentSlug: 'support' },
      },
    ],
  },
  {
    path: 'cause',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform', 'charity'], cache: CachePresets.LISTING } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/home/causes-grid/cause-detail/cause-detail.routes').then(
            (m) => m.CAUSE_DETAIL_ROUTES
          ),
      },
    ],
  },
  {
    path: '404',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform', 'charity'], cache: CachePresets.NO_CACHE } },
    loadComponent: () =>
      import('./features/404-page/404-page.component').then((m) => m.PageNotFoundComponent),
  },
  entityPage('fundraiser/:slug', 'fundraiser', CachePresets.NO_CACHE),
  {
    path: 'team/join/:campaignId',
    canMatch: [siteContextMatch],
    canActivate: [AuthGuard],
    data: { metadata: { app: ['platform', 'charity'], cache: CachePresets.NO_CACHE } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/join-team/join-team.module').then((m) => m.JoinTeamModule),
      },
    ],
  },
  {
    path: 'team/invite/:teamId',
    canMatch: [siteContextMatch],
    canActivate: [AuthGuard],
    data: { metadata: { app: ['platform', 'charity'], cache: CachePresets.NO_CACHE } },
    loadChildren: () => import('./features/create/create.routes').then((m) => m.TEAM_INVITE_ROUTES),
  },
  entityPage('team/:slug', 'team', CachePresets.NO_CACHE),
  {
    path: 'user-donation/:donationId',
    canMatch: [siteContextMatch],
    data: { metadata: { app: ['platform', 'charity'], cache: CachePresets.NO_CACHE } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/user-dashboard/guest-dashboard/guest-dashboard.module').then(
            (m) => m.GuestDashboardModule
          ),
      },
    ],
  },
  // Legacy /campaign/:slug links. Angular rejects canMatch on a redirect; server.ts gives an
  // invalid host's redirect the /404 page body.
  { path: 'campaign/:slug', redirectTo: ({ params }) => AppRoutes.campaign(params['slug']) },
  // One route per page type per URL shape.
  entityPage(':slug', 'campaign', CachePresets.CAMPAIGN_SHELL, true),
  entityPage(':slug', 'fundraiser', CachePresets.NO_CACHE, true),
  entityPage(':slug', 'team', CachePresets.NO_CACHE, true),
  {
    path: ':slug',
    canMatch: [siteContextMatch, slugPageMatch('charity')],
    data: { metadata: { app: ['platform'], cache: CachePresets.HOME } },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/home/charity-profile/charity-profile.routes').then(
            (m) => m.CHARITY_PROFILE_ROUTES
          ),
      },
    ],
  },
  entityPage(':campaign/:slug', 'fundraiser', CachePresets.NO_CACHE, true),
  entityPage(':campaign/:slug', 'team', CachePresets.NO_CACHE, true),
  {
    path: '',
    pathMatch: 'full',
    canMatch: [siteContextMatch],
    data: {
      environment,
      metadata: { app: ['charity'], cache: CachePresets.HOME },
    },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./features/charity-home/charity-home.module').then((m) => m.CharityHomeModule),
      },
    ],
  },
  {
    // Platform home — same shell as charity home. The nav bleeds over the photo
    // hero, so the header is transparent here (route data, not a hardcoded input).
    path: '',
    pathMatch: 'full',
    canMatch: [siteContextMatch],
    data: {
      environment,
      metadata: { app: ['platform'], cache: CachePresets.HOME },
      headerTransparent: true,
    },
    component: LayoutShellComponent,
    children: [
      {
        path: '',
        loadChildren: () => import('./features/home/home.module').then((m) => m.HomeModule),
      },
    ],
  },
  // Redirect, not render: SSR only sets the 404 status for /404.
  { path: '**', redirectTo: AppRoutes.notFound() },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
